/* eslint-disable import/prefer-default-export */
import { OccupantDetailExtended } from '@centecomm/portal-service';
import { useCurrentUser, useTenantBusiness } from '@centecomm/portal-service-react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadStatus } from '../../../Types';
import { Occupant } from '../Types/Occupant';
import { WelcomeActions, WelcomeActionTypes } from '../actions';
import * as selectors from '../selectors';

type WelcomeStateHook = () => {
    areLoaded: boolean;
    areLoading: boolean;
    hasError: boolean;
    hasOccupantToSetup: boolean;
    loadState: LoadStatus;
    occupantsRequiringSetup: Occupant[];
    firstOccupantToSetup: Occupant | undefined;
    welcomeFlowDone: boolean;
};

export const useOccupantsFromWelcomeState: WelcomeStateHook = () => {
    const dispatch = useDispatch();

    const { currentUserOccupantsWithIncompleteSetup: currentUserOccupantsRequiringSetup } = useCurrentUser();

    const { getOccupant } = useTenantBusiness();

    const loadState: LoadStatus = useSelector(selectors.occupantsLoadStatus);

    const occupantsRequiringSetup: Occupant[] = useSelector(selectors.occupantsRequiringSetup);
    const firstOccupantToSetup: Occupant | undefined = useSelector(selectors.occupantToSetup);
    const welcomeFlowDone: boolean = useSelector(selectors.welcomeFlowDone);

    React.useEffect(() => {
        if (loadState === LoadStatus.INITIAL_STATE || loadState === LoadStatus.ERROR) {
            dispatch({
                type: WelcomeActions.LOAD_OCCUPANTS,
            });

            Promise.all<OccupantDetailExtended>(
                currentUserOccupantsRequiringSetup.map((_) => getOccupant(_.occupantId)),
            ).then((values) => {
                let payload = values.map((o) => ({
                    setup: false,
                    id: o.id,
                    name: o.marketingName,
                    phone: o.phone,
                    propertyId: o.propertyId,
                    propertyName: o.propertyName,
                    companyName: o.company.name,
                    physicalAddress: o.physicalAddress,
                    mailingAddress: o.mailingAddress,
                    welcomeFeatureSettings: currentUserOccupantsRequiringSetup.find(os => os.occupantId === o.id)?.welcomeFeatureSettings,
                }));

                //filter out payload that welcomeFeatureSettings.visible is false
                //we only want to add occupants to setup for ones that are explicilty enabled
                payload = payload.filter((o) => o.welcomeFeatureSettings?.visible);

                dispatch({
                    type: WelcomeActions.ADD_OCCUPANTS,
                    payload: payload,
                } as WelcomeActionTypes);
            });
        }
    }, [loadState]);

    return {
        areLoaded: loadState === LoadStatus.LOADED,
        areLoading: loadState === LoadStatus.PENDING,
        hasError: loadState === LoadStatus.ERROR,
        hasOccupantToSetup: occupantsRequiringSetup.length > 0 && !!firstOccupantToSetup,
        loadState,
        occupantsRequiringSetup,
        firstOccupantToSetup,
        welcomeFlowDone,
    };
};
